import { useTranslations } from 'next-intl';
import { Calendar, Layers, Tag, ClipboardList } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { DynamicFormField } from '@/features/dynamic-forms';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatFieldValue(
  field: DynamicFormField,
  value: unknown,
  na: string
): string {
  if (value === null || value === undefined || value === '') return na;

  switch (field.type) {
    case 'yes_no':
      return value ? '✓' : '✗';
    case 'number':
      return Number(value).toLocaleString();
    case 'date':
      return String(value);
    default:
      return String(value);
  }
}

function getFieldIcon(
  type: string
): React.ComponentType<{ className?: string }> {
  switch (type) {
    case 'date':
      return Calendar;
    case 'number':
      return Layers;
    default:
      return Tag;
  }
}

/**
 * Normalize form_responses (keyed by field ID) into a Record.
 */
function normalizeResponses(
  rawResponses: Record<string, unknown> | null
): Record<string, unknown> {
  if (!rawResponses) return {};
  return { ...rawResponses };
}

// ─── Component ───────────────────────────────────────────────────────────────

interface DynamicFormResponsesDisplayProps {
  formResponses: Record<string, unknown> | null;
  fields: DynamicFormField[];
}

export function DynamicFormResponsesDisplay({
  formResponses,
  fields
}: DynamicFormResponsesDisplayProps) {
  const tc = useTranslations('common');

  if (fields.length === 0) return null;

  const normalized = normalizeResponses(formResponses);
  const na = tc('noResults');

  return (
    <Card>
      <CardHeader className='flex flex-row items-center gap-2 pb-4'>
        <ClipboardList className='text-primary h-5 w-5' />
        <CardTitle>{tc('formResponses')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className='grid gap-6 sm:grid-cols-2 lg:grid-cols-3'>
          {fields.map((field) => {
            const key = String(field.id);
            const value = normalized[key];
            const FieldIcon = getFieldIcon(field.type);
            const isLongText = field.type === 'long_text';

            return (
              <div
                key={field.id}
                className={cn(
                  'space-y-1',
                  isLongText && 'sm:col-span-2 lg:col-span-3'
                )}
              >
                <p className='text-muted-foreground text-sm'>{field.label}</p>
                <div className='flex items-center gap-2'>
                  <FieldIcon className='text-muted-foreground h-4 w-4 shrink-0' />
                  <p className='font-semibold'>
                    {formatFieldValue(field, value, na)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
